import type {
  Currency,
  NativeFlowBreakdown,
  NativeFlowBucket,
} from "./portfolio-types.ts";

const flowTolerance = 0.000001;

export type NativeFlowMovement = {
  currency: Currency | null | undefined;
  action: "buy" | "sell" | "convert_in" | "convert_out" | "dividend";
  amount: number;
  weight?: number;
  episodeId?: string | null;
  resolved?: boolean;
};

export type NativeFlowCoverage = {
  knownEpisodeCount: number;
  totalEpisodeCount: number;
};

function normalizedCount(value: number) {
  return Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
}

function normalizedCurrency(value: Currency | null | undefined): Currency | null {
  const currency = String(value ?? "").trim().toUpperCase();
  return currency ? currency as Currency : null;
}

function roundedFlow(value: number) {
  if (Math.abs(value) <= flowTolerance) return 0;
  return Math.round((value + Number.EPSILON) * 100_000_000) / 100_000_000;
}

function statusFor(input: NativeFlowCoverage, unresolvedMovementCount: number): NativeFlowBreakdown["status"] {
  if (input.totalEpisodeCount <= 0) return "unavailable";
  return unresolvedMovementCount <= 0 && input.knownEpisodeCount >= input.totalEpisodeCount
    ? "complete"
    : input.knownEpisodeCount > 0 ? "partial" : "unavailable";
}

export function emptyNativeFlowBreakdown(
  coverage: Partial<NativeFlowCoverage> = {},
): NativeFlowBreakdown {
  const totalEpisodeCount = normalizedCount(coverage.totalEpisodeCount ?? 0);
  const knownEpisodeCount = Math.min(totalEpisodeCount, normalizedCount(coverage.knownEpisodeCount ?? 0));
  return {
    buckets: [],
    status: statusFor({ knownEpisodeCount, totalEpisodeCount }, 0),
    knownEpisodeCount,
    totalEpisodeCount,
    unresolvedMovementCount: 0,
  };
}

export function buildNativeFlowBreakdown(
  movements: NativeFlowMovement[],
  coverage: Partial<NativeFlowCoverage> = {},
): NativeFlowBreakdown {
  const totalEpisodeCount = normalizedCount(coverage.totalEpisodeCount ?? 0);
  const requestedKnownCount = normalizedCount(coverage.knownEpisodeCount ?? totalEpisodeCount);
  const buckets = new Map<Currency, NativeFlowBucket & { episodeIds: Set<string> }>();
  let unresolvedMovementCount = 0;
  const unresolvedEpisodeIds = new Set<string>();
  let unresolvedWithoutEpisodeCount = 0;

  for (const movement of movements) {
    const weight = movement.weight == null ? 1 : Number(movement.weight);
    if (!Number.isFinite(weight) || weight <= 0) continue;
    const currency = normalizedCurrency(movement.currency);
    const amount = Number(movement.amount);
    if (movement.resolved === false || !currency || !Number.isFinite(amount) || amount < 0) {
      unresolvedMovementCount += 1;
      if (movement.episodeId) unresolvedEpisodeIds.add(movement.episodeId);
      else unresolvedWithoutEpisodeCount += 1;
      continue;
    }
    const weightedAmount = roundedFlow(amount * weight);
    if (Math.abs(weightedAmount) <= flowTolerance) continue;
    const current = buckets.get(currency) ?? {
      currency,
      addedAmount: 0,
      reducedAmount: 0,
      incomeAmount: 0,
      netTradeAmount: 0,
      netContributionAmount: 0,
      movementCount: 0,
      knownEpisodeCount: 0,
      totalEpisodeCount: 0,
      episodeIds: new Set<string>(),
    };
    if (movement.action === "buy" || movement.action === "convert_in") current.addedAmount += weightedAmount;
    else if (movement.action === "sell" || movement.action === "convert_out") current.reducedAmount += weightedAmount;
    else current.incomeAmount += weightedAmount;
    current.movementCount += 1;
    if (movement.episodeId) current.episodeIds.add(movement.episodeId);
    buckets.set(currency, current);
  }

  const knownEpisodeCount = Math.min(
    totalEpisodeCount,
    Math.max(0, requestedKnownCount - Math.min(
      requestedKnownCount,
      unresolvedEpisodeIds.size + unresolvedWithoutEpisodeCount,
    )),
  );
  const values = [...buckets.values()].sort((left, right) => left.currency.localeCompare(right.currency)).map((bucket) => {
    const bucketEpisodeCount = bucket.episodeIds.size || (buckets.size === 1 ? totalEpisodeCount : 0);
    const bucketKnownCount = bucket.episodeIds.size || (buckets.size === 1 ? knownEpisodeCount : 0);
    return {
      currency: bucket.currency,
      addedAmount: roundedFlow(bucket.addedAmount),
      reducedAmount: roundedFlow(bucket.reducedAmount),
      incomeAmount: roundedFlow(bucket.incomeAmount),
      netTradeAmount: roundedFlow(bucket.addedAmount - bucket.reducedAmount),
      netContributionAmount: roundedFlow(bucket.addedAmount - bucket.reducedAmount - bucket.incomeAmount),
      movementCount: bucket.movementCount,
      knownEpisodeCount: Math.min(bucketEpisodeCount, bucketKnownCount),
      totalEpisodeCount: bucketEpisodeCount,
    };
  });
  return {
    buckets: values,
    status: statusFor({ knownEpisodeCount, totalEpisodeCount }, unresolvedMovementCount),
    knownEpisodeCount,
    totalEpisodeCount,
    unresolvedMovementCount,
  };
}

export function nativeFlowBreakdownFromAmounts(input: {
  currency: Currency;
  addedAmount?: number;
  reducedAmount?: number;
  incomeAmount?: number;
  movementCount?: number;
  knownEpisodeCount?: number;
  totalEpisodeCount?: number;
}): NativeFlowBreakdown {
  const movements: NativeFlowMovement[] = [];
  const movementCount = Math.max(1, normalizedCount(input.movementCount ?? 1));
  const append = (action: NativeFlowMovement["action"], amount: number | undefined) => {
    if (amount == null || Math.abs(amount) <= flowTolerance) return;
    movements.push({ currency: input.currency, action, amount });
  };
  append("buy", input.addedAmount);
  append("sell", input.reducedAmount);
  append("dividend", input.incomeAmount);
  const result = buildNativeFlowBreakdown(movements, {
    knownEpisodeCount: input.knownEpisodeCount ?? 1,
    totalEpisodeCount: input.totalEpisodeCount ?? 1,
  });
  const bucket = result.buckets[0];
  if (bucket) bucket.movementCount = movementCount;
  return result;
}

export function scaleNativeFlowBreakdown(input: NativeFlowBreakdown, weight: number): NativeFlowBreakdown {
  const normalizedWeight = Number.isFinite(weight) ? Math.max(0, weight) : 0;
  return {
    ...input,
    buckets: input.buckets.map((bucket) => ({
      ...bucket,
      addedAmount: roundedFlow(bucket.addedAmount * normalizedWeight),
      reducedAmount: roundedFlow(bucket.reducedAmount * normalizedWeight),
      incomeAmount: roundedFlow(bucket.incomeAmount * normalizedWeight),
      netTradeAmount: roundedFlow(bucket.netTradeAmount * normalizedWeight),
      netContributionAmount: roundedFlow(bucket.netContributionAmount * normalizedWeight),
    })),
  };
}

export function mergeNativeFlowBreakdowns(
  inputs: Array<NativeFlowBreakdown | null | undefined>,
  coverage?: Partial<NativeFlowCoverage>,
): NativeFlowBreakdown {
  const valid = inputs.filter((input): input is NativeFlowBreakdown => Boolean(input));
  const byCurrency = new Map<Currency, NativeFlowBucket>();
  for (const input of valid) {
    for (const bucket of input.buckets) {
      const current = byCurrency.get(bucket.currency) ?? {
        currency: bucket.currency,
        addedAmount: 0,
        reducedAmount: 0,
        incomeAmount: 0,
        netTradeAmount: 0,
        netContributionAmount: 0,
        movementCount: 0,
        knownEpisodeCount: 0,
        totalEpisodeCount: 0,
      };
      current.addedAmount += bucket.addedAmount;
      current.reducedAmount += bucket.reducedAmount;
      current.incomeAmount += bucket.incomeAmount;
      current.movementCount += bucket.movementCount;
      current.knownEpisodeCount = Math.max(current.knownEpisodeCount, bucket.knownEpisodeCount);
      current.totalEpisodeCount = Math.max(current.totalEpisodeCount, bucket.totalEpisodeCount);
      byCurrency.set(bucket.currency, current);
    }
  }
  const totalEpisodeCount = normalizedCount(coverage?.totalEpisodeCount
    ?? Math.max(0, ...valid.map((input) => input.totalEpisodeCount)));
  const knownEpisodeCount = Math.min(totalEpisodeCount, normalizedCount(coverage?.knownEpisodeCount
    ?? Math.max(0, ...valid.map((input) => input.knownEpisodeCount))));
  const unresolvedMovementCount = valid.reduce((sum, input) => sum + input.unresolvedMovementCount, 0);
  const buckets = [...byCurrency.values()].sort((left, right) => left.currency.localeCompare(right.currency)).map((bucket) => ({
    ...bucket,
    addedAmount: roundedFlow(bucket.addedAmount),
    reducedAmount: roundedFlow(bucket.reducedAmount),
    incomeAmount: roundedFlow(bucket.incomeAmount),
    netTradeAmount: roundedFlow(bucket.addedAmount - bucket.reducedAmount),
    netContributionAmount: roundedFlow(bucket.addedAmount - bucket.reducedAmount - bucket.incomeAmount),
  }));
  return {
    buckets,
    status: statusFor({ knownEpisodeCount, totalEpisodeCount }, unresolvedMovementCount),
    knownEpisodeCount,
    totalEpisodeCount,
    unresolvedMovementCount,
  };
}

export function withNativeFlowCoverage(
  input: NativeFlowBreakdown,
  coverage: NativeFlowCoverage,
  currencyCoverage: ReadonlyMap<Currency, NativeFlowCoverage> = new Map(),
): NativeFlowBreakdown {
  const totalEpisodeCount = normalizedCount(coverage.totalEpisodeCount);
  const knownEpisodeCount = Math.min(totalEpisodeCount, normalizedCount(coverage.knownEpisodeCount));
  return {
    ...input,
    status: statusFor({ knownEpisodeCount, totalEpisodeCount }, input.unresolvedMovementCount),
    knownEpisodeCount,
    totalEpisodeCount,
    buckets: input.buckets.map((bucket) => {
      const bucketCoverage = currencyCoverage.get(bucket.currency);
      if (!bucketCoverage) return input.buckets.length === 1
        ? { ...bucket, knownEpisodeCount, totalEpisodeCount }
        : bucket;
      const bucketTotal = normalizedCount(bucketCoverage.totalEpisodeCount);
      return {
        ...bucket,
        knownEpisodeCount: Math.min(bucketTotal, normalizedCount(bucketCoverage.knownEpisodeCount)),
        totalEpisodeCount: bucketTotal,
      };
    }),
  };
}
